import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ArrowUpRight, BarChart3, CalendarDays, ChevronDown, Crown, LogIn, Menu, MessageCircle,
    PackageOpen, Receipt, UsersRound, Wallet, X,
} from 'lucide-react';
import styles from './Navbar.module.css';
import logoNavy from '../../assets/hirsta-logo-navy.png';
import logoWhite from '../../assets/hirsta-logo-white.png';
import Button from '../Button/Button';
import { SITE } from '../../config/site';

// Modules shown in the Features menu (each opens its section on /features)
const MODULES = [
    { id: 'billing', icon: Receipt, name: 'Smart billing', text: 'GST bills, split payments, khata' },
    { id: 'memberships', icon: Crown, name: 'Memberships', text: 'Prepaid cards and discount plans' },
    { id: 'packages', icon: PackageOpen, name: 'Packages', text: 'Sell once, track every visit' },
    { id: 'appointments', icon: CalendarDays, name: 'Appointments', text: 'Calendar and WhatsApp reminders' },
    { id: 'clients', icon: Wallet, name: 'Clients and khata', text: 'Profiles, dues and advance' },
    { id: 'staff', icon: UsersRound, name: 'Staff and payroll', text: 'Attendance, incentives, credit' },
    { id: 'analytics', icon: BarChart3, name: 'Analytics', text: 'Dashboard and GST report' },
    { id: 'marketing', icon: MessageCircle, name: 'WhatsApp marketing', text: 'Campaigns to the right clients' },
];

const LINKS = [
    { label: 'Packages', to: '/features#packages' },
    { label: 'Grow sales', to: '/#growth' },
    { label: 'Contact', to: '/demo' },
];

// Pages whose top section is dark: the bar starts transparent with white text there
const DARK_TOP = ['/'];

export default function Navbar() {
    const { pathname, hash } = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const megaRef = useRef(null);
    const closeTimer = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // close menus when the page changes
    useEffect(() => { setMobileOpen(false); setMegaOpen(false); }, [pathname, hash]);

    // Esc closes; click outside closes the Features menu; no page scroll behind the mobile menu
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') { setMegaOpen(false); setMobileOpen(false); } };
        const onClick = (e) => { if (megaRef.current && !megaRef.current.contains(e.target)) setMegaOpen(false); };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
    }, []);
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const openMega = () => { clearTimeout(closeTimer.current); setMegaOpen(true); };
    const closeMegaSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setMegaOpen(false), 140); };

    const onDark = DARK_TOP.includes(pathname) && !scrolled && !megaOpen && !mobileOpen;
    const featuresActive = pathname === '/features' && hash !== '#packages';

    return (
        <header className={`${styles.header} ${onDark ? styles.onDark : styles.solid} ${scrolled ? styles.scrolled : ''}`}>
            <a href="#main" className={styles.skip}>Skip to content</a>
            <div className={styles.inner}>
                <Link to="/" className={styles.brand} aria-label="Hirsta home">
                    <img src={onDark ? logoWhite : logoNavy} alt="Hirsta" />
                </Link>

                <nav className={styles.nav} aria-label="Main">
                    <ul className={styles.links}>
                        <li ref={megaRef} className={styles.megaItem} onMouseEnter={openMega} onMouseLeave={closeMegaSoon}>
                            <button
                                type="button"
                                className={`${styles.link} ${featuresActive ? styles.active : ''}`}
                                aria-expanded={megaOpen}
                                aria-controls="features-menu"
                                onClick={() => setMegaOpen((v) => !v)}
                            >
                                {featuresActive && <motion.span layoutId="nav-pill" className={styles.pill} transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
                                <span className={styles.linkText}>Features</span>
                                <ChevronDown size={14} className={`${styles.chev} ${megaOpen ? styles.chevOpen : ''}`} />
                            </button>

                            <AnimatePresence>
                                {megaOpen && (
                                    <motion.div
                                        id="features-menu"
                                        className={styles.mega}
                                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.12 } }}
                                        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                                        onMouseEnter={openMega}
                                        onMouseLeave={closeMegaSoon}
                                    >
                                        <div className={styles.megaBody}>
                                        <div className={styles.megaGrid}>
                                            {MODULES.map((m) => {
                                                const Icon = m.icon;
                                                return (
                                                    <Link key={m.id} to={`/features#${m.id}`} className={styles.megaLink}>
                                                        <span className={styles.megaIcon}><Icon size={16} strokeWidth={2.2} /></span>
                                                        <span className={styles.megaText}>
                                                            <strong>{m.name}</strong>
                                                            <small>{m.text}</small>
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>

                                        {/* featured: packages */}
                                        <Link to="/features#packages" className={styles.feature}>
                                            <span className={styles.featureTag}>New</span>
                                            <strong>Service packages</strong>
                                            <small>Sell once, then track every visit until it is used up.</small>
                                            <span className={styles.featureLink}>See how it works <ArrowUpRight size={13} /></span>
                                        </Link>
                                        </div>
                                        <div className={styles.megaFoot}>
                                            <span>Every module shares one login and one database.</span>
                                            <Link to="/features" className={styles.megaAll}>All features <ArrowUpRight size={13} /></Link>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </li>

                        {LINKS.map((l) => {
                            const isActive = l.to === '/demo' && pathname === '/demo';
                            return (
                                <li key={l.label}>
                                    <NavLink to={l.to} className={`${styles.link} ${isActive ? styles.active : ''}`}>
                                        {isActive && <motion.span layoutId="nav-pill" className={styles.pill} transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
                                        <span className={styles.linkText}>{l.label}</span>
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                <div className={styles.actions}>
                    <a href={SITE.loginUrl} className={styles.login} target="_blank" rel="noreferrer">Log in</a>
                    <Button to="/demo" variant={onDark ? 'light' : 'primary'}>Book a demo</Button>
                </div>

                <button
                    type="button"
                    className={styles.burger}
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-expanded={mobileOpen}
                    aria-controls="mobile-menu"
                    aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                >
                    {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {/* ---------------- mobile menu ---------------- */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        id="mobile-menu"
                        className={styles.mobile}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10, transition: { duration: 0.15 } }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <p className={styles.mobileLabel}>Features</p>
                        <div className={styles.mobileGrid}>
                            {MODULES.map((m) => {
                                const Icon = m.icon;
                                return (
                                    <Link key={m.id} to={`/features#${m.id}`} className={styles.mobileModule}>
                                        <span className={styles.megaIcon}><Icon size={15} /></span>
                                        <span>{m.name}</span>
                                    </Link>
                                );
                            })}
                        </div>
                        <div className={styles.mobileLinks}>
                            <Link to="/features">All features</Link>
                            {LINKS.map((l) => <Link key={l.label} to={l.to}>{l.label}</Link>)}
                        </div>
                        <div className={styles.mobileActions}>
                            <Button to="/demo" size="lg" icon={ArrowUpRight}>Book a free demo</Button>
                            <a href={SITE.loginUrl} className={styles.mobileLogin} target="_blank" rel="noreferrer"><LogIn size={15} /> Log in to Hirsta</a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
